import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { logger } from "../../utils/logger";
// BigInt replacer function for JSON.stringify
const bigIntReplacer = (key: string, value: any) => {
    if (typeof value === 'bigint') {
        return value.toString();
    }
    return value;
};
// Types
interface VenueFormData {
    name: string;
    type_id: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    city_other: string;
    zipcode_other: string;
    phone: string;
    email: string;
    website: string;
    description: string;
    capacity: string;
    amenities: string;
    parking_type: string;
    parking_details: string;
    security_level: string;
    security_details: string;
    cell_signal_quality: string;
    restaurant_proximity: string;
    restaurant_details: string;
    airport_distance_miles: string;
    highway_access: string;
    hotel_proximity: string;
    hotel_details: string;
    public_transit_access: boolean;
    transit_details: string;
    quiet_environment: boolean;
    natural_lighting: boolean;
    air_purification: boolean;
    accessibility_features: string;
    environmental_notes: string;
    venue_notes: string;
    image_url: string;
    pricing: string;
    availability: string;
    food_beverage_details: string;
    latitude: string;
    longitude: string;
    status: boolean;
}
interface MeetingRoom {
    id: number | string;
    name: string;
    capacity: string;
    description: string;
    amenities: string;
    setup_style: string;
    audio_visual_equipment: string;
    accessibility_features: string;
    notes: string;
}
interface VenueState {
    venues: any[];
    venueTypes: any[];
    venueAmenities: any[];
    venueFormData: VenueFormData;
    editingVenue: any | null;
    venueImages: any[];
    meetingRooms: MeetingRoom[];
    roomImages: {
        [key: string]: any[];
    };
    roomAmenities: {
        [key: string]: string[];
    };
    roomNewAmenity: {
        [key: string]: string;
    };
    selectedAmenities: string[];
    newAmenity: string;
    callForPricing: boolean;
    callForAvailability: boolean;
    pendingImageFiles: File[];
    pendingImagePreviews: string[];
    pendingImageSeoData: Array<{
        altText: string;
        title: string;
        description: string;
        caption: string;
    }>;
    // SEO edit modal state
    seoEditModalOpen: boolean;
    editingImageId: number | null;
    editingImage: any | null;
    seoAltText: string;
    seoTitle: string;
    seoDescription: string;
    seoCaption: string;
    // Loading states
    venuesLoading: boolean;
    venueSubmitting: boolean;
    error: string | null;
}
// Initial form data
const initialFormData: VenueFormData = {
    name: '',
    type_id: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    city_other: '',
    zipcode_other: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    capacity: '',
    amenities: '',
    parking_type: '',
    parking_details: '',
    security_level: '',
    security_details: '',
    cell_signal_quality: '',
    restaurant_proximity: '',
    restaurant_details: '',
    airport_distance_miles: '',
    highway_access: '',
    hotel_proximity: '',
    hotel_details: '',
    public_transit_access: false,
    transit_details: '',
    quiet_environment: false,
    natural_lighting: false,
    air_purification: false,
    accessibility_features: '',
    environmental_notes: '',
    venue_notes: '',
    image_url: '',
    pricing: '',
    availability: '',
    food_beverage_details: '',
    latitude: '',
    longitude: '',
    status: true
};
// Initial state
const initialState: VenueState = {
    venues: [],
    venueTypes: [],
    venueAmenities: [],
    venueFormData: initialFormData,
    editingVenue: null,
    venueImages: [],
    meetingRooms: [],
    roomImages: {},
    roomAmenities: {},
    roomNewAmenity: {},
    selectedAmenities: [],
    newAmenity: '',
    callForPricing: false,
    callForAvailability: false,
    pendingImageFiles: [],
    pendingImagePreviews: [],
    pendingImageSeoData: [],
    seoEditModalOpen: false,
    editingImageId: null,
    editingImage: null,
    seoAltText: '',
    seoTitle: '',
    seoDescription: '',
    seoCaption: '',
    venuesLoading: true,
    venueSubmitting: false,
    error: null,
};
// Async thunks
export const loadVenues = createAsyncThunk('venue/loadVenues', async (_, { rejectWithValue }) => {
    try {
        const response = await fetch('/api/venues/training-manager', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to load venues: ${response.status} ${errorText}`);
        }
        const result = await response.json();
        if (result.success) {
            return result.data || [];
        }
        else {
            throw new Error(result.error || 'Failed to load venues');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to load venues');
    }
});
export const loadVenueTypes = createAsyncThunk('venue/loadVenueTypes', async (_, { rejectWithValue }) => {
    try {
        const response = await fetch('/api/venues/types', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to load venue types');
        }
        const result = await response.json();
        if (result.success) {
            return result.data || [];
        }
        else {
            throw new Error(result.error || 'Failed to load venue types');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to load venue types');
    }
});
export const loadVenueAmenities = createAsyncThunk('venue/loadVenueAmenities', async (_, { rejectWithValue }) => {
    try {
        const response = await fetch('/api/venues/amenities', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to load venue amenities');
        }
        const result = await response.json();
        if (result.success) {
            return result.data || [];
        }
        else {
            throw new Error(result.error || 'Failed to load venue amenities');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to load venue amenities');
    }
});
export const loadVenueImages = createAsyncThunk('venue/loadVenueImages', async (venueId: string | number, { rejectWithValue }) => {
    try {
        const response = await fetch(`/api/venues/${venueId}/images`, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to load venue images');
        }
        const result = await response.json();
        if (result.success) {
            const formattedImages = result.data.map((img: any) => ({
                id: img.id,
                url: img.image_url,
                alt_text: img.alt_text,
                title: img.title,
                description: img.description,
                caption: img.caption,
                filename: img.alt_text || `Image ${img.id}`,
                isMain: img.is_main,
                sort_order: img.sort_order
            }));
            return formattedImages;
        }
        else {
            throw new Error(result.error || 'Failed to load venue images');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to load venue images');
    }
});
export const loadMeetingRooms = createAsyncThunk('venue/loadMeetingRooms', async (venueId: string | number, { rejectWithValue }) => {
    try {
        const response = await fetch(`/api/venues/${venueId}/rooms`, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to load meeting rooms');
        }
        const result = await response.json();
        if (result.success) {
            const formattedRooms = result.data.map((room: any) => ({
                id: room.id,
                name: room.name || '',
                capacity: room.capacity?.toString() || '',
                description: room.description || '',
                amenities: room.amenities || '',
                setup_style: room.setup_style || '',
                audio_visual_equipment: room.audio_visual_equipment || '',
                accessibility_features: room.accessibility_features || '',
                notes: room.notes || ''
            }));
            return formattedRooms;
        }
        else {
            throw new Error(result.error || 'Failed to load meeting rooms');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to load meeting rooms');
    }
});
export const createVenue = createAsyncThunk('venue/createVenue', async (payload: {
    formData: VenueFormData;
    geoData: any;
    selectedAmenities: string[];
}, { rejectWithValue }) => {
    try {
        const { formData, geoData, selectedAmenities } = payload;
        // Check authentication
        const authCheckResponse = await fetch('/api/auth/me', {
            method: 'GET',
            credentials: 'include'
        });
        if (!authCheckResponse.ok) {
            throw new Error('Your session has expired. Redirecting to login...');
        }
        const venuePayload = {
            ...formData,
            airport_distance_miles: parseFloat(formData.airport_distance_miles) || null,
            type_id: parseInt(formData.type_id) || 1,
            capacity: parseInt(formData.capacity) || 0,
            state_id: geoData.state_id,
            county_id: geoData.county_id,
            city_id: geoData.city_id,
            zipcode_id: geoData.zipcode_id,
            amenities: selectedAmenities,
            name: formData.name || '',
            address: formData.address || '',
        };
        const response = await fetch('/api/venues/admin', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(venuePayload, bigIntReplacer),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            if (response.status === 401) {
                throw new Error('Your session has expired. Please refresh the page and log in again.');
            }
            else if (response.status === 403) {
                throw new Error('You do not have permission to create venues. Please contact your administrator.');
            }
            else {
                const errorMessage = errorData.details || errorData.error || `Failed to create venue (${response.status})`;
                throw new Error(errorMessage);
            }
        }
        const result = await response.json();
        if (result.success) {
            return result.data;
        }
        else {
            throw new Error(result.error || 'Failed to create venue');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to create venue');
    }
});
export const updateVenue = createAsyncThunk('venue/updateVenue', async (payload: {
    venueId: string | number;
    formData: VenueFormData;
    geoData: any;
    selectedAmenities: string[];
    meetingRooms: MeetingRoom[];
}, { rejectWithValue }) => {
    try {
        const { venueId, formData, geoData, selectedAmenities, meetingRooms } = payload;
        // Check authentication
        const authCheckResponse = await fetch('/api/auth/me', {
            method: 'GET',
            credentials: 'include'
        });
        if (!authCheckResponse.ok) {
            throw new Error('Your session has expired. Redirecting to login...');
        }
        const venuePayload = {
            ...formData,
            airport_distance_miles: parseFloat(formData.airport_distance_miles) || null,
            type_id: parseInt(formData.type_id) || 1,
            capacity: parseInt(formData.capacity) || 0,
            state_id: geoData.state_id,
            county_id: geoData.county_id,
            city_id: geoData.city_id,
            zipcode_id: geoData.zipcode_id,
            amenities: selectedAmenities,
            name: formData.name || '',
            address: formData.address || '',
        };
        const response = await fetch(`/api/venues/admin/${venueId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(venuePayload, bigIntReplacer),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            if (response.status === 401) {
                throw new Error('Your session has expired. Please refresh the page and log in again.');
            }
            else if (response.status === 403) {
                throw new Error('You do not have permission to update venues. Please contact your administrator.');
            }
            else {
                const errorMessage = errorData.details || errorData.error || `Failed to update venue (${response.status})`;
                throw new Error(errorMessage);
            }
        }
        const result = await response.json();
        if (result.success) {
            // Handle meeting rooms separately
            await updateMeetingRoomsForVenue(venueId, meetingRooms);
            return result.data;
        }
        else {
            throw new Error(result.error || 'Failed to update venue');
        }
    }
    catch (error: any) {
        return rejectWithValue(error.message || 'Failed to update venue');
    }
});
// Helper function for updating meeting rooms
async function updateMeetingRoomsForVenue(venueId: string | number, meetingRooms: MeetingRoom[]) {
    try {
        for (const room of meetingRooms) {
            if (!room.name)
                continue;
            const roomData = {
                name: room.name,
                capacity: room.capacity ? parseInt(room.capacity) : null,
                description: room.description || null,
                amenities: room.amenities || null,
                setup_style: room.setup_style || null,
                audio_visual_equipment: room.audio_visual_equipment || null,
                accessibility_features: room.accessibility_features || null,
                notes: room.notes || null
            };
            if (room.id && typeof room.id === 'string' && room.id !== 'new') {
                // Update existing room
                await fetch(`/api/venues/${venueId}/rooms/${room.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(roomData),
                });
            }
            else {
                // Create new room
                await fetch(`/api/venues/${venueId}/rooms`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(roomData),
                });
            }
        }
    }
    catch (error) {
        logger.error('Error updating meeting rooms:', error, {});
    }
}
// Slice
const venueSlice = createSlice({
    name: 'venue',
    initialState,
    reducers: {
        setVenueFormData: (state, action: PayloadAction<Partial<VenueFormData>>) => {
            state.venueFormData = { ...state.venueFormData, ...action.payload };
        },
        setEditingVenue: (state, action: PayloadAction<any | null>) => {
            state.editingVenue = action.payload;
            if (action.payload) {
                // Populate form with venue data
                state.venueFormData = {
                    name: action.payload.name || '',
                    type_id: action.payload.type_id?.toString() || '',
                    address: action.payload.address || '',
                    city: action.payload.city || '',
                    state: action.payload.state || '',
                    zip_code: action.payload.zip_code || '',
                    city_other: action.payload.city_other || '',
                    zipcode_other: action.payload.zipcode_other || '',
                    phone: action.payload.phone || '',
                    email: action.payload.email || '',
                    website: action.payload.website || '',
                    description: action.payload.description || '',
                    capacity: action.payload.capacity?.toString() || '',
                    amenities: action.payload.amenities || '',
                    parking_type: action.payload.parking_type || '',
                    parking_details: action.payload.parking_details || '',
                    security_level: action.payload.security_level || '',
                    security_details: action.payload.security_details || '',
                    cell_signal_quality: action.payload.cell_signal_quality || '',
                    restaurant_proximity: action.payload.restaurant_proximity || '',
                    restaurant_details: action.payload.restaurant_details || '',
                    airport_distance_miles: action.payload.airport_distance_miles?.toString() || '',
                    highway_access: action.payload.highway_access || '',
                    hotel_proximity: action.payload.hotel_proximity || '',
                    hotel_details: action.payload.hotel_details || '',
                    public_transit_access: action.payload.public_transit_access || false,
                    transit_details: action.payload.transit_details || '',
                    quiet_environment: action.payload.quiet_environment || false,
                    natural_lighting: action.payload.natural_lighting || false,
                    air_purification: action.payload.air_purification || false,
                    accessibility_features: action.payload.accessibility_features || '',
                    environmental_notes: action.payload.environmental_notes || '',
                    venue_notes: action.payload.venue_notes || '',
                    image_url: action.payload.image_url || '',
                    pricing: action.payload.pricing || '',
                    availability: action.payload.availability || '',
                    food_beverage_details: action.payload.food_beverage_details || '',
                    latitude: action.payload.latitude?.toString() || '',
                    longitude: action.payload.longitude?.toString() || '',
                    status: action.payload.status !== undefined ? action.payload.status : true
                };
                // Set pricing and availability flags
                state.callForPricing = action.payload.pricing === 'Call for Pricing';
                state.callForAvailability = action.payload.availability === 'Call for Availability';
                // Set amenities
                try {
                    if (Array.isArray(action.payload.amenities)) {
                        state.selectedAmenities = action.payload.amenities;
                    }
                    else if (typeof action.payload.amenities === 'string' && action.payload.amenities.trim()) {
                        state.selectedAmenities = JSON.parse(action.payload.amenities);
                    }
                    else {
                        state.selectedAmenities = [];
                    }
                }
                catch (error) {
                    state.selectedAmenities = [];
                }
            }
        },
        resetVenueForm: (state) => {
            state.venueFormData = initialFormData;
            state.editingVenue = null;
            state.selectedAmenities = [];
            state.newAmenity = '';
            state.callForPricing = false;
            state.callForAvailability = false;
            state.venueImages = [];
            state.meetingRooms = [];
            state.roomImages = {};
            state.roomAmenities = {};
            state.roomNewAmenity = {};
            state.error = null;
        },
        setSelectedAmenities: (state, action: PayloadAction<string[]>) => {
            state.selectedAmenities = action.payload;
        },
        addAmenity: (state, action: PayloadAction<string>) => {
            if (action.payload.trim() && !state.selectedAmenities.includes(action.payload.trim())) {
                state.selectedAmenities.push(action.payload.trim());
                state.newAmenity = '';
            }
        },
        removeAmenity: (state, action: PayloadAction<string>) => {
            state.selectedAmenities = state.selectedAmenities.filter(amenity => amenity !== action.payload);
        },
        setNewAmenity: (state, action: PayloadAction<string>) => {
            state.newAmenity = action.payload;
        },
        setCallForPricing: (state, action: PayloadAction<boolean>) => {
            state.callForPricing = action.payload;
            if (action.payload) {
                state.venueFormData.pricing = 'Call for Pricing';
            }
            else {
                state.venueFormData.pricing = '';
            }
        },
        setCallForAvailability: (state, action: PayloadAction<boolean>) => {
            state.callForAvailability = action.payload;
            if (action.payload) {
                state.venueFormData.availability = 'Call for Availability';
            }
            else {
                state.venueFormData.availability = '';
            }
        },
        setVenueImages: (state, action: PayloadAction<any[]>) => {
            state.venueImages = action.payload;
        },
        setMeetingRooms: (state, action: PayloadAction<MeetingRoom[]>) => {
            state.meetingRooms = action.payload;
        },
        addMeetingRoom: (state, action: PayloadAction<MeetingRoom>) => {
            state.meetingRooms.push(action.payload);
        },
        updateMeetingRoom: (state, action: PayloadAction<{
            index: number;
            room: Partial<MeetingRoom>;
        }>) => {
            const { index, room } = action.payload;
            if (state.meetingRooms[index]) {
                state.meetingRooms[index] = { ...state.meetingRooms[index], ...room };
            }
        },
        removeMeetingRoom: (state, action: PayloadAction<number>) => {
            state.meetingRooms.splice(action.payload, 1);
        },
        setRoomAmenities: (state, action: PayloadAction<{
            [key: string]: string[];
        }>) => {
            state.roomAmenities = action.payload;
        },
        setRoomNewAmenity: (state, action: PayloadAction<{
            [key: string]: string;
        }>) => {
            state.roomNewAmenity = action.payload;
        },
        // SEO edit modal actions
        setSeoEditModalOpen: (state, action: PayloadAction<boolean>) => {
            state.seoEditModalOpen = action.payload;
        },
        setEditingImage: (state, action: PayloadAction<{
            id: number;
            image: any;
        } | null>) => {
            if (action.payload) {
                state.editingImageId = action.payload.id;
                state.editingImage = action.payload.image;
                state.seoAltText = action.payload.image.alt_text || '';
                state.seoTitle = action.payload.image.title || '';
                state.seoDescription = action.payload.image.description || '';
                state.seoCaption = action.payload.image.caption || '';
            }
            else {
                state.editingImageId = null;
                state.editingImage = null;
                state.seoAltText = '';
                state.seoTitle = '';
                state.seoDescription = '';
                state.seoCaption = '';
            }
        },
        setSeoData: (state, action: PayloadAction<{
            altText?: string;
            title?: string;
            description?: string;
            caption?: string;
        }>) => {
            if (action.payload.altText !== undefined)
                state.seoAltText = action.payload.altText;
            if (action.payload.title !== undefined)
                state.seoTitle = action.payload.title;
            if (action.payload.description !== undefined)
                state.seoDescription = action.payload.description;
            if (action.payload.caption !== undefined)
                state.seoCaption = action.payload.caption;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        resetVenueState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // Load venues
            .addCase(loadVenues.pending, (state) => {
            state.venuesLoading = true;
            state.error = null;
        })
            .addCase(loadVenues.fulfilled, (state, action) => {
            state.venuesLoading = false;
            state.venues = action.payload;
            state.error = null;
        })
            .addCase(loadVenues.rejected, (state, action) => {
            state.venuesLoading = false;
            state.error = action.payload as string;
        })
            // Load venue types
            .addCase(loadVenueTypes.fulfilled, (state, action) => {
            state.venueTypes = action.payload;
        })
            // Load venue amenities
            .addCase(loadVenueAmenities.fulfilled, (state, action) => {
            state.venueAmenities = action.payload;
        })
            // Load venue images
            .addCase(loadVenueImages.fulfilled, (state, action) => {
            state.venueImages = action.payload;
        })
            .addCase(loadVenueImages.rejected, (state) => {
            state.venueImages = [];
        })
            // Load meeting rooms
            .addCase(loadMeetingRooms.fulfilled, (state, action) => {
            state.meetingRooms = action.payload;
        })
            .addCase(loadMeetingRooms.rejected, (state) => {
            state.meetingRooms = [];
        })
            // Create venue
            .addCase(createVenue.pending, (state) => {
            state.venueSubmitting = true;
            state.error = null;
        })
            .addCase(createVenue.fulfilled, (state) => {
            state.venueSubmitting = false;
            state.error = null;
        })
            .addCase(createVenue.rejected, (state, action) => {
            state.venueSubmitting = false;
            state.error = action.payload as string;
        })
            // Update venue
            .addCase(updateVenue.pending, (state) => {
            state.venueSubmitting = true;
            state.error = null;
        })
            .addCase(updateVenue.fulfilled, (state) => {
            state.venueSubmitting = false;
            state.error = null;
        })
            .addCase(updateVenue.rejected, (state, action) => {
            state.venueSubmitting = false;
            state.error = action.payload as string;
        });
    },
});
// Export actions
export const { setVenueFormData, setEditingVenue, resetVenueForm, setSelectedAmenities, addAmenity, removeAmenity, setNewAmenity, setCallForPricing, setCallForAvailability, setVenueImages, setMeetingRooms, addMeetingRoom, updateMeetingRoom, removeMeetingRoom, setRoomAmenities, setRoomNewAmenity, setSeoEditModalOpen, setEditingImage, setSeoData, setError, resetVenueState, } = venueSlice.actions;
// Export selectors
export const selectVenues = (state: {
    venue: VenueState;
}) => state.venue.venues;
export const selectVenueTypes = (state: {
    venue: VenueState;
}) => state.venue.venueTypes;
export const selectVenueAmenities = (state: {
    venue: VenueState;
}) => state.venue.venueAmenities;
export const selectVenueFormData = (state: {
    venue: VenueState;
}) => state.venue.venueFormData;
export const selectEditingVenue = (state: {
    venue: VenueState;
}) => state.venue.editingVenue;
export const selectVenueImages = (state: {
    venue: VenueState;
}) => state.venue.venueImages;
export const selectMeetingRooms = (state: {
    venue: VenueState;
}) => state.venue.meetingRooms;
export const selectSelectedAmenities = (state: {
    venue: VenueState;
}) => state.venue.selectedAmenities;
export const selectVenueFlags = createSelector([(state: {
        venue: VenueState;
    }) => state.venue], (venue) => ({
    callForPricing: venue.callForPricing,
    callForAvailability: venue.callForAvailability,
}));
export const selectVenueLoading = createSelector([(state: {
        venue: VenueState;
    }) => state.venue], (venue) => ({
    venuesLoading: venue.venuesLoading,
    venueSubmitting: venue.venueSubmitting,
}));
export const selectSeoEditModal = (state: {
    venue: VenueState;
}) => ({
    open: state.venue.seoEditModalOpen,
    editingImageId: state.venue.editingImageId,
    editingImage: state.venue.editingImage,
    altText: state.venue.seoAltText,
    title: state.venue.seoTitle,
    description: state.venue.seoDescription,
    caption: state.venue.seoCaption,
});
// Export reducer
export default venueSlice.reducer;
